<!-- src/components/inputs/DatePicker.vue -->
<template>
  <div ref="containerRef" class="relative">
    <!-- Input field -->
    <button
      type="button"
      class="w-full flex items-center gap-3 pl-3 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-50 hover:bg-gray-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent transition-colors text-left"
      :class="{ 'ring-2 ring-bc-indigo-500': isOpen }"
      @click="toggle"
    >
      <div
        class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        :class="iconBgClass"
      >
        <font-awesome-icon :icon="iconName" :class="iconClass" class="text-sm" />
      </div>
      <div class="flex-1 min-w-0">
        <p v-if="modelValue" class="font-medium text-gray-900 truncate">
          {{ formattedDate }}
        </p>
        <p v-else class="text-gray-400">
          {{ placeholder }}
        </p>
        <p v-if="modelValue" class="text-xs text-gray-500 mt-0.5">
          {{ formattedFullDate }}
        </p>
      </div>
      <font-awesome-icon
        :icon="['fas', 'chevron-down']"
        class="text-gray-400 text-xs transition-transform"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Calendar dropdown -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="isOpen"
        class="absolute z-50 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
        :class="dropdownPosition"
      >
        <!-- Month/Year header -->
        <div
          class="flex items-center justify-between p-3 bg-gradient-to-r from-bc-indigo-500 to-bc-indigo-600"
        >
          <button
            type="button"
            class="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            @click="prevMonth"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-sm" />
          </button>
          <div class="text-center">
            <p class="font-semibold text-white">{{ monthYearLabel }}</p>
          </div>
          <button
            type="button"
            class="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            @click="nextMonth"
          >
            <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-sm" />
          </button>
        </div>

        <!-- Quick actions -->
        <div class="flex gap-2 p-3 border-b border-gray-100">
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium text-bc-indigo-600 bg-bc-indigo-50 hover:bg-bc-indigo-100 rounded-lg transition-colors"
            @click="selectToday"
          >
            Today
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            @click="selectNextMonday"
          >
            Next Monday
          </button>
        </div>

        <!-- Day headers -->
        <div class="grid grid-cols-7 gap-1 px-3 pt-2">
          <div
            v-for="day in dayNames"
            :key="day"
            class="text-center text-xs font-medium text-gray-400 py-1"
          >
            {{ day }}
          </div>
        </div>

        <!-- Calendar grid -->
        <div class="grid grid-cols-7 gap-1 p-3">
          <button
            v-for="(day, index) in calendarDays"
            :key="index"
            type="button"
            :disabled="!day.date || day.isDisabled"
            class="aspect-square rounded-lg text-sm font-medium transition-all flex items-center justify-center"
            :class="getDayClass(day)"
            @click="day.date && selectDate(day.date)"
          >
            {{ day.dayNumber }}
          </button>
        </div>

        <!-- Footer with clear -->
        <div v-if="modelValue" class="px-3 pb-3">
          <button
            type="button"
            class="w-full py-2 text-xs font-medium text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            @click="clearDate"
          >
            Clear date
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { nextOccurrenceOf, toLocalDateKey } from '@/utils/date'

const props = withDefaults(
  defineProps<{
    modelValue: string // ISO date string YYYY-MM-DD
    placeholder?: string
    icon?: 'calendar' | 'flag'
    minDate?: string
    maxDate?: string
  }>(),
  {
    placeholder: 'Select date',
    icon: 'calendar',
    minDate: undefined,
    maxDate: undefined,
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const containerRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const viewDate = ref(new Date())

const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const iconName = computed(() => {
  return props.icon === 'flag' ? ['fas', 'flag-checkered'] : ['fas', 'calendar']
})

const iconBgClass = computed(() => {
  return props.icon === 'flag' ? 'bg-purple-100' : 'bg-bc-indigo-100'
})

const iconClass = computed(() => {
  return props.icon === 'flag' ? 'text-purple-600' : 'text-bc-indigo-600'
})

const dropdownPosition = computed(() => {
  // Could add logic to position above if near bottom of viewport
  return 'left-0'
})

const formattedDate = computed(() => {
  if (!props.modelValue) return ''
  const date = new Date(props.modelValue + 'T00:00:00')
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})

const formattedFullDate = computed(() => {
  if (!props.modelValue) return ''
  const date = new Date(props.modelValue + 'T00:00:00')
  return date.toLocaleDateString('en-US', { weekday: 'long' })
})

const monthYearLabel = computed(() => {
  return viewDate.value.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
})

interface CalendarDay {
  date: Date | null
  dayNumber: string
  isCurrentMonth: boolean
  isToday: boolean
  isSelected: boolean
  isDisabled: boolean
}

const calendarDays = computed((): CalendarDay[] => {
  const year = viewDate.value.getFullYear()
  const month = viewDate.value.getMonth()

  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startPadding = firstDay.getDay()

  const days: CalendarDay[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const selectedDate = props.modelValue ? new Date(props.modelValue + 'T00:00:00') : null

  // Add empty cells for padding
  for (let i = 0; i < startPadding; i++) {
    days.push({
      date: null,
      dayNumber: '',
      isCurrentMonth: false,
      isToday: false,
      isSelected: false,
      isDisabled: true,
    })
  }

  // Add days of the month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d)
    const isToday = date.getTime() === today.getTime()
    const isSelected = selectedDate ? date.getTime() === selectedDate.getTime() : false

    let isDisabled = false
    if (props.minDate) {
      const min = new Date(props.minDate + 'T00:00:00')
      if (date < min) isDisabled = true
    }
    if (props.maxDate) {
      const max = new Date(props.maxDate + 'T00:00:00')
      if (date > max) isDisabled = true
    }

    days.push({
      date,
      dayNumber: String(d),
      isCurrentMonth: true,
      isToday,
      isSelected,
      isDisabled,
    })
  }

  return days
})

function getDayClass(day: CalendarDay): string {
  if (!day.date) return 'invisible'

  if (day.isDisabled) {
    return 'text-gray-300 cursor-not-allowed'
  }

  if (day.isSelected) {
    return 'bg-bc-indigo-500 text-white shadow-md hover:bg-bc-indigo-600'
  }

  if (day.isToday) {
    return 'bg-bc-indigo-100 text-bc-indigo-700 font-semibold hover:bg-bc-indigo-200'
  }

  return 'text-gray-700 hover:bg-gray-100'
}

function toggle() {
  isOpen.value = !isOpen.value
  if (isOpen.value && props.modelValue) {
    viewDate.value = new Date(props.modelValue + 'T00:00:00')
  } else if (isOpen.value) {
    viewDate.value = new Date()
  }
}

function close() {
  isOpen.value = false
}

function prevMonth() {
  const newDate = new Date(viewDate.value)
  newDate.setMonth(newDate.getMonth() - 1)
  viewDate.value = newDate
}

function nextMonth() {
  const newDate = new Date(viewDate.value)
  newDate.setMonth(newDate.getMonth() + 1)
  viewDate.value = newDate
}

function selectDate(date: Date) {
  emit('update:modelValue', toLocalDateKey(date))
  close()
}

function selectToday() {
  selectDate(new Date())
}

function selectNextMonday() {
  selectDate(nextOccurrenceOf('MONDAY'))
}

function clearDate() {
  emit('update:modelValue', '')
  close()
}

function handleClickOutside(event: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Sync viewDate when modelValue changes externally
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && !isOpen.value) {
      viewDate.value = new Date(newVal + 'T00:00:00')
    }
  }
)
</script>
